import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  CurrencyRupeeIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  PrinterIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import axios from '../services/api';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import { MessageCircle } from 'lucide-react';

const RUPEE_SYMBOL = '\u20B9';
const BILLING_WORKFLOW_STORAGE_PREFIX = 'billing-workflow';

function formatDisplayDate(dateValue) {
  if (!dateValue) return '-';

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return String(dateValue);

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
}

function formatCurrency(amount) {
  const value = Number(amount || 0);
  return `${RUPEE_SYMBOL}${value.toFixed(2)}`;
}

function normalizePhone(phone) {
  if (!phone) return '';

  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  let local = digits;
  if (local.length === 12 && local.startsWith('91')) local = local.slice(2);
  if (local.length === 11 && local.startsWith('0')) local = local.slice(1);
  return /^[6-9]\d{9}$/.test(local) ? `91${local}` : '';
}

function createPrescriptionRow(prescription) {
  return {
    id: prescription.id,
    type: 'prescription',
    patientName: prescription.Patient?.name || 'Unknown Patient',
    phone: prescription.Patient?.phone || '-',
    customerId: prescription.Patient?.patientId || '-',
    reference: `RX-${String(prescription.id).padStart(5, '0')}`,
    date: prescription.date || prescription.createdAt || '',
    createdAt: prescription.createdAt || prescription.date || '',
    summary: prescription.status ? prescription.status.charAt(0).toUpperCase() + prescription.status.slice(1) : 'Prescription',
  };
}

function createInvoiceRow(invoice) {
  return {
    id: invoice.id,
    type: 'invoice',
    patientName: invoice.Patient?.name || 'Unknown Patient',
    phone: invoice.Patient?.phone || '-',
    customerId: invoice.Patient?.patientId || '-',
    reference: invoice.invoiceNumber || `INV-${String(invoice.id).padStart(5, '0')}`,
    date: invoice.date || invoice.createdAt || '',
    createdAt: invoice.createdAt || invoice.date || '',
    summary: `${invoice.paymentType || 'Pending'} | ${formatCurrency(invoice.totalAmount)}`,
  };
}

function getRowSortTime(row) {
  const primaryValue = row?.createdAt || row?.date || '';
  const parsedTime = new Date(primaryValue).getTime();

  if (Number.isNaN(parsedTime)) {
    return 0;
  }

  return parsedTime;
}

async function printPdfBlob(blob) {
  const blobUrl = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    iframe.style.pointerEvents = 'none';

    iframe.onload = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
            iframe.remove();
          }, 60000);
        }
      }, 250);
    };

    iframe.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      iframe.remove();
      reject(new Error('Failed to load PDF for printing'));
    };

    iframe.src = blobUrl;
    document.body.appendChild(iframe);
  });
}

function openBlobInNewTab(blob) {
  const blobUrl = URL.createObjectURL(blob);
  const newWindow = window.open(blobUrl, '_blank');

  if (!newWindow) {
    URL.revokeObjectURL(blobUrl);
    throw new Error('Please allow pop-ups to open the printable PDF.');
  }

  try {
    newWindow.opener = null;
  } catch (_error) {
    // Ignore browser restrictions here; the PDF is already open.
  }

  setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, 60000);
}

export default function PrintDocuments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore(state => state.user);
  const canReadClinical = user?.capabilities?.includes('clinical.view');
  const canReadInvoices = user?.capabilities?.includes('billing.invoice.create');
  const [activeTab, setActiveTab] = useState(canReadClinical ? 'prescription' : 'invoice');
  const [clinicData, setClinicData] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [documentActionLoadingKey, setDocumentActionLoadingKey] = useState('');
  const [manageLinkDoc, setManageLinkDoc] = useState(null);
  const [secureLink, setSecureLink] = useState({ url:'', status:'revoked', expiresAt:null });
  const [postBillingSummary, setPostBillingSummary] = useState(null);
  const [returnCountdown, setReturnCountdown] = useState(null);
  const autoOpenedRef = useRef(false);
  const isPostBillingWorkflow = searchParams.get('workflow') === 'post-billing';
  const workflowInvoiceId = searchParams.get('invoiceId');
  const workflowPrescriptionId = searchParams.get('prescriptionId');
  const shouldReturnToDashboard =
    isPostBillingWorkflow && searchParams.get('returnToDashboard') === '1';

  const clearStoredWorkflowSummary = () => {
    if (!workflowInvoiceId) {
      return;
    }

    try {
      sessionStorage.removeItem(`${BILLING_WORKFLOW_STORAGE_PREFIX}:${workflowInvoiceId}`);
    } catch (error) {
      console.warn('Unable to clear billing workflow summary:', error);
    }
  };

  const returnToDashboard = () => {
    clearStoredWorkflowSummary();
    navigate('/dashboard', { replace: true });
  };

  const fetchLists = async (showRefreshState = false) => {
    if (showRefreshState) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [setupRes, prescriptionRes, invoiceRes] = await Promise.all([
        axios.get('/setup').catch(() => ({ data: null })),
        canReadClinical ? axios.get('/prescriptions') : Promise.resolve({ data: [] }),
        canReadInvoices ? axios.get('/invoices') : Promise.resolve({ data: [] }),
      ]);

      setClinicData(setupRes.data || null);
      setPrescriptions(Array.isArray(prescriptionRes.data) ? prescriptionRes.data : []);
      setInvoices(Array.isArray(invoiceRes.data) ? invoiceRes.data : []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load print documents');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLists(false);
  }, []);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if ((requestedTab === 'invoice' && canReadInvoices) || (requestedTab === 'prescription' && canReadClinical)) {
      setActiveTab(requestedTab);
    }
  }, [searchParams]);

  const prescriptionRows = useMemo(
    () =>
      prescriptions
        .map((item) => createPrescriptionRow(item))
        .sort((left, right) => getRowSortTime(right) - getRowSortTime(left)),
    [prescriptions]
  );

  const invoiceRows = useMemo(
    () =>
      invoices
        .map((item) => createInvoiceRow(item))
        .sort((left, right) => getRowSortTime(right) - getRowSortTime(left)),
    [invoices]
  );

  const activeRows = activeTab === 'prescription' ? prescriptionRows : invoiceRows;

  const filteredRows = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    if (!searchLower) return activeRows;

    return activeRows.filter((item) => {
      const patientName = String(item.patientName || '').toLowerCase();
      const phone = String(item.phone || '');
      const reference = String(item.reference || '').toLowerCase();
      const customerId = String(item.customerId || '').toLowerCase();

      return (
        patientName.includes(searchLower) ||
        phone.includes(searchTerm.trim()) ||
        reference.includes(searchLower) ||
        customerId.includes(searchLower)
      );
    });
  }, [activeRows, searchTerm]);

  const fetchDocumentDetail = async (type, id) => {
    if (type === 'prescription' ? !canReadClinical : !canReadInvoices) throw new Error('Document access is not permitted');
    const endpoint = type === 'prescription' ? `/prescriptions/${id}` : `/invoices/${id}`;
    const response = await axios.get(endpoint);
    return response.data;
  };

  const fetchDocumentPdfBlob = async (type, id) => {
    if (type === 'prescription' ? !canReadClinical : !canReadInvoices) throw new Error('Document access is not permitted');
    const endpoint = type === 'prescription' ? `/prescriptions/${id}/pdf` : `/invoices/${id}/pdf`;
    const response = await axios.get(endpoint, { responseType: 'blob' });
    return new Blob([response.data], { type: 'application/pdf' });
  };

  const openDocumentModal = async (row) => {
    setSelectedDoc({ type: row.type, reference: row.reference, data: null });
    setDetailsLoading(true);

    try {
      const detail = await fetchDocumentDetail(row.type, row.id);
      setSelectedDoc({ type: row.type, reference: row.reference, data: detail });
    } catch (error) {
      console.error(error);
      toast.error(`Failed to load ${row.type}`);
      setSelectedDoc(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    const shouldAutoFocus = searchParams.get('autofocus') === '1';
    if (!shouldAutoFocus || loading || autoOpenedRef.current) {
      return;
    }

    const requestedTab = searchParams.get('tab');
    const requestedInvoiceId = searchParams.get('invoiceId');
    const requestedPrescriptionId = searchParams.get('prescriptionId');

    let targetRow = null;

    if (requestedTab === 'invoice' && requestedInvoiceId) {
      setActiveTab('invoice');
      targetRow = invoiceRows.find((row) => String(row.id) === String(requestedInvoiceId)) || null;
    } else if (requestedTab === 'prescription' && requestedPrescriptionId) {
      if (canReadClinical) setActiveTab('prescription');
      targetRow =
        prescriptionRows.find((row) => String(row.id) === String(requestedPrescriptionId)) || null;
    } else if (requestedInvoiceId) {
      setActiveTab('invoice');
      targetRow = invoiceRows.find((row) => String(row.id) === String(requestedInvoiceId)) || null;
    } else if (requestedPrescriptionId) {
      if (canReadClinical) setActiveTab('prescription');
      targetRow =
        prescriptionRows.find((row) => String(row.id) === String(requestedPrescriptionId)) || null;
    }

    if (!targetRow) {
      return;
    }

    autoOpenedRef.current = true;
    openDocumentModal(targetRow);
  }, [invoiceRows, loading, prescriptionRows, searchParams]);

  useEffect(() => {
    if (!isPostBillingWorkflow) {
      setPostBillingSummary(null);
      setReturnCountdown(null);
      return;
    }

    let parsedSummary = null;
    if (workflowInvoiceId) {
      try {
        const storedValue = sessionStorage.getItem(
          `${BILLING_WORKFLOW_STORAGE_PREFIX}:${workflowInvoiceId}`
        );
        parsedSummary = storedValue ? JSON.parse(storedValue) : null;
      } catch (error) {
        console.warn('Unable to read billing workflow summary:', error);
      }
    }

    setPostBillingSummary(
      parsedSummary || {
        invoiceId: workflowInvoiceId ? Number(workflowInvoiceId) : null,
        prescriptionId: workflowPrescriptionId || '',
        deliveryState: 'unknown',
        delivered: false,
        deliveryMessage:
          'Billing workflow completed. Inventory Audit, printable records, and Dashboard handoff are ready.',
      }
    );
    setReturnCountdown(shouldReturnToDashboard ? 8 : null);
  }, [isPostBillingWorkflow, shouldReturnToDashboard, workflowInvoiceId, workflowPrescriptionId]);

  useEffect(() => {
    if (!shouldReturnToDashboard || returnCountdown === null) {
      return undefined;
    }

    if (returnCountdown <= 0) {
      clearStoredWorkflowSummary();
      navigate('/dashboard', { replace: true });
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setReturnCountdown((current) => (current === null ? current : current - 1));
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [navigate, returnCountdown, shouldReturnToDashboard, workflowInvoiceId]);

  const runDocumentAction = async (type, id, mode) => {
    const actionKey = `${mode}:${type}:${id}`;
    setDocumentActionLoadingKey(actionKey);

    try {
      const pdfBlob = await fetchDocumentPdfBlob(type, id);

      if (mode === 'print') {
        try {
          await printPdfBlob(pdfBlob);
          toast.success('Print dialog opened');
        } catch (error) {
          console.error(error);
          openBlobInNewTab(pdfBlob);
          toast.success('Printable PDF opened');
        }
      } else if (mode === 'download') {
        const url=URL.createObjectURL(pdfBlob); const anchor=document.createElement('a'); anchor.href=url; anchor.download=`${type}-${id}.pdf`; anchor.click(); URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } else {
        openBlobInNewTab(pdfBlob);
        toast.success('Printable PDF opened');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || `Failed to ${mode} document`);
    } finally {
      setDocumentActionLoadingKey('');
    }
  };

  const createSecureLink = async (row) => {
    const { data } = await axios.post(`/secure-patient-documents/${row.type}/${row.id}`);
    setSecureLink(data);
    return data.url;
  };
  const openManageLink = async (row) => {
    setManageLinkDoc(row); setSecureLink({ url:'', status:'revoked', expiresAt:null });
    try { const { data }=await axios.get(`/secure-patient-documents/${row.type}/${row.id}`); setSecureLink(data); } catch (error) { toast.error(error.response?.data?.error || 'Secure link status could not be loaded.'); }
  };
  const shareRowOnWhatsApp = async (row) => {
    const phone=normalizePhone(row.phone);
    if (!phone) { toast.error('Patient mobile number is unavailable.'); return; }
    try {
      const url=await createSecureLink(row); const clinic=clinicData?.clinicLegalName || clinicData?.legalName || 'our clinic';
      const message=row.type==='prescription' ? `Your prescription from ${clinic} is ready.\n\nView or download your prescription securely:\n${url}` : `Your invoice from ${clinic} is ready.\n\nView or download your invoice securely:\n${url}`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`,'_blank','noopener,noreferrer');
    } catch (error) { toast.error(error.response?.data?.error || 'Secure link could not be created.'); }
  };

  const handleWhatsApp = async () => {
    const patientPhone = selectedDoc?.data?.Patient?.phone;
    const normalizedPhone = normalizePhone(patientPhone);

    if (!normalizedPhone) {
      toast.error('Patient mobile number is unavailable.');
      return;
    }
    await shareRowOnWhatsApp({ id:selectedDoc.data.id, type:selectedDoc.type, phone:patientPhone });
  };

  const renderPrescriptionDetail = (prescription) => {
    const treatments = prescription?.PrescriptionTreatments || [];

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient ID</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{prescription.Patient?.patientId || '-'}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Date</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">
              {formatDisplayDate(prescription.date || prescription.createdAt)}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Status</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">
              {prescription.status ? prescription.status.charAt(0).toUpperCase() + prescription.status.slice(1) : '-'}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Medical History</p>
          <p className="mt-3 text-sm leading-6 text-slate-700">
            {String(prescription.medicalHistory || '').trim() || 'No medical history recorded.'}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Observations</p>
          <p className="mt-3 text-sm leading-6 text-slate-700">
            {String(prescription.remarks || '').trim() || 'No observations recorded.'}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Treatment Plan</p>
          {treatments.length > 0 ? (
            <div className="mt-3 space-y-2.5">
              {treatments.map((treatment, index) => (
                <div
                  key={treatment.id || `${treatment.treatmentId}-${index}`}
                  className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {index + 1}. {treatment.Treatment?.name || 'Treatment'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        {treatment.toothNumber ? `Tooth ${treatment.toothNumber}` : 'Tooth not specified'}
                      </p>
                    </div>
                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-600">
                      {treatment.status || 'planned'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">No treatment entries found.</p>
          )}
        </div>
      </div>
    );
  };

  const renderInvoiceDetail = (invoice) => {
    const treatments = invoice?.InvoiceTreatments || [];

    return (
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Invoice No</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{invoice.invoiceNumber || '-'}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Date</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">
              {formatDisplayDate(invoice.date || invoice.createdAt)}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Payment Type</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{invoice.paymentType || '-'}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Total Amount</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{formatCurrency(invoice.totalAmount)}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Billing Items</p>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold text-slate-600">
              {invoice.status || 'Pending'}
            </span>
          </div>

          {treatments.length > 0 ? (
            <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
              <table className="w-full text-left">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Treatment</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Tooth</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Qty</th>
                    <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {treatments.map((item) => {
                    const amount = Number(item.quantity || 1) * Number(item.priceAtTime || 0);

                    return (
                      <tr key={item.id}>
                        <td className="px-4 py-3.5 text-sm font-medium text-slate-900">
                          {item.Treatment?.name || 'Treatment'}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-slate-600">{item.toothNumber || '-'}</td>
                        <td className="px-4 py-3.5 text-sm text-slate-600">{item.quantity || 1}</td>
                        <td className="px-4 py-3.5 text-right text-sm font-semibold text-slate-900">
                          {formatCurrency(amount)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">No billed treatment lines found.</p>
          )}
        </div>

        {String(invoice.remarks || '').trim() ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Remarks</p>
            <p className="mt-3 text-sm leading-6 text-slate-700">{invoice.remarks}</p>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/60 px-4 py-6 md:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-5">
        {isPostBillingWorkflow ? (
          <section className="rounded-[24px] border border-emerald-200 bg-emerald-50/80 px-5 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                  Billing Workflow Complete
                </div>
                <h2 className="text-lg font-semibold tracking-tight text-emerald-900">
                  Inventory Audit is finalized and printable records are ready.
                </h2>
                <p className="max-w-3xl text-sm text-emerald-900/80">
                  {postBillingSummary?.deliveryMessage ||
                    'Billing records were saved successfully. The billing-message flow has finished and the latest print records are available at the top of this page.'}
                </p>
                {postBillingSummary?.whatsappWarning ? (
                  <p className="max-w-3xl text-sm text-amber-800">
                    {postBillingSummary.whatsappWarning}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
                  <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 text-emerald-700">
                    Inventory Audit Finalized
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">
                    WhatsApp:{' '}
                    {postBillingSummary?.whatsappWebOpened
                      ? 'WhatsApp Web Opened'
                      : postBillingSummary?.delivered
                      ? 'Sent'
                      : postBillingSummary?.deliveryState || 'Prepared'}
                  </span>
                  {workflowInvoiceId ? (
                    <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-slate-700">
                      Invoice #{postBillingSummary?.invoiceNumber || workflowInvoiceId}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {workflowInvoiceId ? (
                  <button
                    type="button"
                    onClick={() => runDocumentAction('invoice', workflowInvoiceId, 'open')}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    Open Invoice PDF
                  </button>
                ) : null}

                {postBillingSummary?.whatsappWebUrl ? (
                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        postBillingSummary.whatsappWebUrl,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Open WhatsApp Web
                  </button>
                ) : null}

                {workflowPrescriptionId ? (
                  <button
                    type="button"
                    onClick={() => runDocumentAction('prescription', workflowPrescriptionId, 'open')}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                    Open Prescription PDF
                  </button>
                ) : null}

                {returnCountdown !== null ? (
                  <button
                    type="button"
                    onClick={() => setReturnCountdown(null)}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
                  >
                    Stay Here
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={returnToDashboard}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700"
                >
                  {returnCountdown !== null
                    ? `Return to Dashboard (${returnCountdown}s)`
                    : 'Return to Dashboard'}
                </button>
              </div>
            </div>
          </section>
        ) : null}

        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">Document Center</p>
            <h1 className="text-[28px] font-semibold tracking-tight text-slate-900 md:text-[2.1rem]">
              Print Documents
            </h1>
            <p className="text-sm text-slate-500">Open prescriptions and invoices, then print the real PDF for patients.</p>
          </div>

          <button
            type="button"
            onClick={() => fetchLists(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-[0_6px_16px_rgba(15,23,42,0.04)] transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Lists'}
          </button>
        </div>

        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
          <button
            type="button"
            hidden={!canReadClinical}
            onClick={() => {
              if (canReadClinical) setActiveTab('prescription');
              setSelectedDoc(null);
            }}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
              activeTab === 'prescription' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <DocumentTextIcon className="h-5 w-5" />
            Prescriptions
          </button>
          <button
            type="button"
            hidden={!canReadInvoices}
            onClick={() => {
              setActiveTab('invoice');
              setSelectedDoc(null);
            }}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
              activeTab === 'invoice' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ClipboardDocumentListIcon className="h-5 w-5" />
            Invoices
          </button>
        </div>

        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
          <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-sm font-semibold tracking-tight text-slate-900">
                {activeTab === 'prescription' ? 'Prescription List' : 'Invoice List'}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                {activeTab === 'prescription'
                  ? 'Open the real prescription document or print it directly.'
                  : 'Open the real invoice document or print it directly.'}
              </p>
            </div>

            <div className="relative w-full md:w-[360px]">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by patient, phone, ID, or reference"
                className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-100"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] border-collapse">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Contact</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Reference</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Date</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Summary</th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-5 py-14 text-center text-sm text-slate-500">
                      Loading documents...
                    </td>
                  </tr>
                ) : filteredRows.length > 0 ? (
                  filteredRows.map((row) => {
                    const printActionKey = `download:${row.type}:${row.id}`;

                    return (
                      <tr key={`${row.type}-${row.id}`} className="transition hover:bg-slate-50/80">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700">
                              {row.patientName?.charAt(0) || 'P'}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{row.patientName}</p>
                              <p className="mt-0.5 text-xs text-slate-500">{row.customerId !== '-' ? row.customerId : 'No patient ID'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600">{row.phone}</td>
                        <td className="px-5 py-4 text-sm font-medium text-slate-700">{row.reference}</td>
                        <td className="px-5 py-4 text-sm text-slate-600">{formatDisplayDate(row.date)}</td>
                        <td className="px-5 py-4 text-sm text-slate-600">{row.summary}</td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openDocumentModal(row)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700"
                            >
                              <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() => runDocumentAction(row.type, row.id, 'download')}
                              disabled={documentActionLoadingKey === printActionKey}
                              className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 transition hover:bg-blue-100 disabled:opacity-60"
                            >
                              <PrinterIcon className="h-4 w-4" />
                              {documentActionLoadingKey === printActionKey ? 'Downloading...' : 'Download PDF'}
                            </button>
                            <span className="inline-flex flex-col items-end gap-1"><button type="button" disabled={!normalizePhone(row.phone)} onClick={() => shareRowOnWhatsApp(row)} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"><MessageCircle className="h-4 w-4" />Send on WhatsApp</button>{!normalizePhone(row.phone) ? <span className="text-[10px] font-medium text-amber-700">Patient mobile number is unavailable.</span> : null}</span>
                            <button type="button" onClick={() => openManageLink(row)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700">Manage Link</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="px-5 py-14 text-center">
                      <div className="mx-auto max-w-md space-y-2">
                        <p className="text-sm font-semibold text-slate-900">No documents found</p>
                        <p className="text-sm text-slate-500">
                          {activeTab === 'prescription'
                            ? 'No prescriptions match the current search.'
                            : 'No invoices match the current search.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {manageLinkDoc ? <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"><section className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl"><div className="flex items-center justify-between"><h2 className="text-xl font-bold">Manage Link</h2><button type="button" onClick={() => setManageLinkDoc(null)}>Close</button></div><p className="mt-4 text-sm font-semibold">Link Status: <span className="capitalize text-teal-700">{secureLink.status}</span></p><div className="mt-5 flex flex-wrap gap-2"><button type="button" onClick={async()=>{try{await createSecureLink(manageLinkDoc);toast.success('Secure link regenerated.');}catch(error){toast.error(error.response?.data?.error || 'Secure link could not be regenerated.');}}} className="rounded-xl border px-4 py-2 font-semibold">Regenerate Link</button><button type="button" onClick={async()=>{try{const {data}=await axios.post(`/secure-patient-documents/${manageLinkDoc.type}/${manageLinkDoc.id}/disable`);setSecureLink({url:'',...data});toast.success('Secure link disabled.');}catch(error){toast.error(error.response?.data?.error || 'Secure link could not be disabled.');}}} className="rounded-xl border border-rose-200 px-4 py-2 font-semibold text-rose-700">Disable Link</button></div></section></div>:null}

      {selectedDoc ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-5xl overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.14)]">
            <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 md:flex-row md:items-start md:justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  {selectedDoc.type === 'invoice' ? (
                    <ClipboardDocumentListIcon className="h-5 w-5" />
                  ) : (
                    <DocumentTextIcon className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600">
                    {selectedDoc.type === 'invoice' ? 'Invoice View' : 'Prescription View'}
                  </p>
                  <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">
                    {selectedDoc.data?.Patient?.name || 'Loading document...'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    {selectedDoc.reference}
                    {selectedDoc.data?.date || selectedDoc.data?.createdAt
                      ? ` | ${formatDisplayDate(selectedDoc.data?.date || selectedDoc.data?.createdAt)}`
                      : ''}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {selectedDoc.data ? (
                  <>
                    <button
                      type="button"
                      onClick={() => runDocumentAction(selectedDoc.type, selectedDoc.data.id, 'open')}
                      disabled={documentActionLoadingKey === `open:${selectedDoc.type}:${selectedDoc.data.id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      {documentActionLoadingKey === `open:${selectedDoc.type}:${selectedDoc.data.id}` ? 'Opening...' : 'Open PDF'}
                    </button>
                    <button
                      type="button"
                      onClick={() => runDocumentAction(selectedDoc.type, selectedDoc.data.id, 'print')}
                      disabled={documentActionLoadingKey === `print:${selectedDoc.type}:${selectedDoc.data.id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3.5 py-2 text-sm font-medium text-blue-700 transition hover:bg-blue-100 disabled:opacity-60"
                    >
                      <PrinterIcon className="h-4 w-4" />
                      {documentActionLoadingKey === `print:${selectedDoc.type}:${selectedDoc.data.id}` ? 'Opening...' : 'Print'}
                    </button>
                    <button
                      type="button"
                      onClick={handleWhatsApp}
                      disabled={!normalizePhone(selectedDoc.data?.Patient?.phone)}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Send on WhatsApp
                    </button>
                    {!normalizePhone(selectedDoc.data?.Patient?.phone) ? <span className="text-xs font-medium text-amber-700">Patient mobile number is unavailable.</span> : null}
                  </>
                ) : null}
                <button
                  type="button"
                  onClick={() => setSelectedDoc(null)}
                  className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="max-h-[80vh] overflow-y-auto bg-slate-50/60 px-5 py-5">
              {detailsLoading || !selectedDoc.data ? (
                <div className="flex min-h-[320px] items-center justify-center rounded-[22px] border border-dashed border-slate-200 bg-white">
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-900">Loading document...</p>
                    <p className="mt-1 text-sm text-slate-500">Fetching the latest printable record.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <div className="rounded-[20px] border border-slate-200 bg-white px-4 py-3.5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Patient</p>
                      <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <UserIcon className="h-4 w-4 text-slate-400" />
                        {selectedDoc.data.Patient?.name || '-'}
                      </p>
                    </div>
                    <div className="rounded-[20px] border border-slate-200 bg-white px-4 py-3.5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Phone</p>
                      <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <PhoneIcon className="h-4 w-4 text-slate-400" />
                        {selectedDoc.data.Patient?.phone || '-'}
                      </p>
                    </div>
                    <div className="rounded-[20px] border border-slate-200 bg-white px-4 py-3.5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Date</p>
                      <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                        {formatDisplayDate(selectedDoc.data.date || selectedDoc.data.createdAt)}
                      </p>
                    </div>
                    <div className="rounded-[20px] border border-slate-200 bg-white px-4 py-3.5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        {selectedDoc.type === 'invoice' ? 'Amount' : 'Reference'}
                      </p>
                      <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
                        {selectedDoc.type === 'invoice' ? (
                          <>
                            <CurrencyRupeeIcon className="h-4 w-4 text-slate-400" />
                            {formatCurrency(selectedDoc.data.totalAmount)}
                          </>
                        ) : (
                          selectedDoc.reference
                        )}
                      </p>
                    </div>
                  </div>

                  {selectedDoc.type === 'invoice'
                    ? renderInvoiceDetail(selectedDoc.data)
                    : renderPrescriptionDetail(selectedDoc.data)}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
